import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import api from '../api';

export default function Dashboard() {
  const { user, tenant } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="page-wrap">
      <div className="empty-state"><div className="empty-state-icon">⏳</div><div className="empty-state-title">Loading dashboard...</div></div>
    </div>
  );

  const h = data?.wo_health || {};
  const feed = data?.activity_feed || [];
  const atRisk = data?.at_risk_list || [];
  const approvals = data?.approvals_queue || [];
  const monWatch = data?.monitoring_watch || [];
  const team = data?.team_workload || [];
  const clients = data?.client_snapshot || [];
  const costs = data?.cost_snapshot;

  return (
    <div className="page-wrap">
      {/* Page header */}
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-sub">{tenant?.name} · {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</div>
        </div>
        <button className="btn btn-secondary" onClick={() => window.location.reload()}>🔄 Refresh</button>
      </div>

      {/* WO Health Strip */}
      <div className="grid-4 mb-16">
        <div className="kpi-card red" onClick={() => navigate('/pipeline')}>
          <div className="kpi-label">⚠️ At Risk</div>
          <div className="kpi-value">{h.at_risk || 0}</div>
          <div className="kpi-sub">WOs due in 3 days</div>
        </div>
        <div className="kpi-card yellow" onClick={() => navigate('/content')}>
          <div className="kpi-label">⏳ Awaiting Approval</div>
          <div className="kpi-value">{h.awaiting_approval || 0}</div>
          <div className="kpi-sub">Pending review</div>
        </div>
        <div className="kpi-card blue" onClick={() => navigate('/monitoring')}>
          <div className="kpi-label">📡 Posted & Monitoring</div>
          <div className="kpi-value">{h.monitoring || 0}</div>
          <div className="kpi-sub">Active monitoring</div>
        </div>
        <div className="kpi-card green" onClick={() => navigate('/pipeline')}>
          <div className="kpi-label">✅ Closed (Month)</div>
          <div className="kpi-value">{h.closed_this_month || 0}</div>
          <div className="kpi-sub">of {h.total_this_month || 0} this month</div>
        </div>
      </div>

      {/* Row 1: At Risk + Team Workload */}
      <div className="grid-2 mb-16">
        <div className="card">
          <div className="card-header">
            <div className="card-title">⚠️ Work Orders At Risk</div>
            <span className="badge badge-red">{atRisk.length}</span>
          </div>
          {atRisk.length === 0 ? (
            <div className="text-sm text-muted" style={{ padding: '12px 0' }}>No work orders at risk. 🎉</div>
          ) : atRisk.map(wo => (
            <div key={wo.id} className="list-item" style={{ cursor: 'pointer' }} onClick={() => navigate('/pipeline')}>
              <div style={{ flex: 1 }}>
                <div className="fw-600 text-sm">{wo.wo_number} — {wo.title}</div>
                <div className="text-xs text-muted">{wo.client} · {wo.event_date}</div>
              </div>
              <span className={`badge ${wo.overdue ? 'badge-red' : 'badge-yellow'}`}>
                {wo.overdue ? 'Overdue' : 'Due Soon'}
              </span>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">👥 Team Workload</div>
            <button className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '4px 10px' }} onClick={() => navigate('/team')}>View All</button>
          </div>
          {team.length === 0 ? (
            <div className="text-sm text-muted">No team data.</div>
          ) : team.map(m => (
            <div key={m.id} className="list-item">
              <div className="avatar" style={{ background: m.color }}>{m.initials}</div>
              <div style={{ flex: 1 }}>
                <div className="fw-600 text-sm">{m.name}</div>
                <div className="text-xs text-muted">{m.open_tasks} open tasks</div>
              </div>
              <div className="progress-bar" style={{ width: '60px' }}>
                <div className="progress-fill" style={{ width: `${Math.min(100, (m.open_tasks / 10) * 100)}%` }} />
              </div>
              <span className={`badge ${m.availability === 'available' ? 'badge-green' : m.availability === 'busy' ? 'badge-yellow' : 'badge-red'}`}>
                {m.availability}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Row 2: Client Snapshot + Approvals Queue */}
      <div className="grid-2 mb-16">
        <div className="card">
          <div className="card-header">
            <div className="card-title">🏢 Client Snapshot</div>
          </div>
          {clients.length === 0 ? (
            <div className="text-sm text-muted">No clients yet.</div>
          ) : clients.map(c => (
            <div key={c.id} className="list-item">
              <div className="avatar" style={{ background: c.color || '#3b7ef0' }}>{c.name.charAt(0)}</div>
              <div style={{ flex: 1 }}>
                <div className="fw-600 text-sm">{c.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                  <div className="progress-bar" style={{ flex: 1 }}>
                    <div className="progress-fill" style={{ width: `${c.budget_pct}%`, background: c.budget_pct > 90 ? 'var(--red)' : c.budget_pct > 70 ? 'var(--yellow)' : 'var(--accent)' }} />
                  </div>
                  <span className="text-xs text-muted mono">{c.budget_pct}%</span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="text-xs text-muted">Budget Used</div>
                <div className="text-xs mono fw-600">₹{Number(c.spent).toLocaleString('en-IN')} / ₹{Number(c.budget).toLocaleString('en-IN')}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">✅ Approvals Queue</div>
            <span className="badge badge-yellow">{approvals.length}</span>
          </div>
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {approvals.length === 0 ? (
              <div className="text-sm text-muted">No pending approvals.</div>
            ) : approvals.map((item, i) => (
              <div key={i} className="list-item" style={{ cursor: 'pointer' }} onClick={() => navigate('/' + item.link)}>
                <div style={{ flex: 1 }}>
                  <div className="fw-600 text-sm">{item.wo_number} — {item.title}</div>
                  <div className="text-xs text-muted">{item.module} · {item.client}</div>
                </div>
                <span className="badge badge-yellow">{item.stage}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3: Monitoring Watch + Activity Feed */}
      <div className="grid-2 mb-16">
        <div className="card">
          <div className="card-header">
            <div className="card-title">📡 Monitoring Watch</div>
            <span className="badge badge-red">{monWatch.length}</span>
          </div>
          {monWatch.length === 0 ? (
            <div className="text-sm text-muted">All clear — no issues flagged.</div>
          ) : monWatch.map(item => (
            <div key={item.work_order_id} className="list-item" style={{ cursor: 'pointer' }} onClick={() => navigate('/monitoring')}>
              <div style={{ flex: 1 }}>
                <div className="fw-600 text-sm">{item.wo_number} — {item.title}</div>
                <div className="text-xs text-muted">{item.client}</div>
              </div>
              <span className={`badge ${item.status === 'Action Required' ? 'badge-red' : 'badge-yellow'}`}>{item.status}</span>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">⚡ Live Activity Feed</div>
            <div className="icon-btn" onClick={() => window.location.reload()}>🔄</div>
          </div>
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {feed.length === 0 ? (
              <div className="text-sm text-muted">No activity yet.</div>
            ) : feed.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent)', marginTop: '5px', flexShrink: 0 }} />
                <div>
                  <div className="text-sm"><strong>{item.user_name}</strong> {item.action.replace(/_/g, ' ')} <span className="text-muted">{item.entity_label}</span></div>
                  <div className="text-xs text-muted" style={{ marginTop: '2px' }}>{new Date(item.occurred_at).toLocaleString('en-IN')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Cost Snapshot */}
      {costs && (
        <div className="card mb-16">
          <div className="card-header">
            <div className="card-title">💰 Cost Snapshot — {new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</div>
            <button className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '4px 10px' }} onClick={() => navigate('/costs')}>View All</button>
          </div>
          <div className="grid-3 mb-12">
            {(costs.categories || []).slice(0, 3).map(cat => (
              <div key={cat.category}>
                <div className="text-xs text-muted mb-8">{cat.category}</div>
                <div className="progress-bar mb-8">
                  <div className="progress-fill" style={{ width: `${cat.pct}%`, background: cat.pct > 100 ? 'var(--red)' : 'var(--accent)' }} />
                </div>
                <div className="flex-between">
                  <span className="text-xs text-muted mono">₹{Number(cat.actual).toLocaleString('en-IN')}</span>
                  <span className="text-xs text-muted mono">/ ₹{Number(cat.planned).toLocaleString('en-IN')}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="sep" />
          <div className="flex-between">
            <span className="text-sm fw-700">Total Spent</span>
            <span className="mono fw-700" style={{ color: 'var(--yellow)' }}>
              ₹{Number(costs.total_actual).toLocaleString('en-IN')} / ₹{Number(costs.total_planned).toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
